import { isAndroidPackageName } from "../apps/androidAppIcon";
import type { NativeSelectionSummary } from "../native/plugins";
import { canPickAndroidApps, canPickScreenTimeApps } from "../native/rules";
import { selectionHasContent, targetAppKey } from "../rules/ruleDerivation";
import type { Rule, Target, TargetApp, TargetConfig, TargetSelection } from "../rules/ruleTypes";

export type IconAppsPlatform = "ios" | "android" | "web";

/** Runtime platform for icon resolution — pass explicitly in unit tests. */
export function resolveIconAppsPlatform(): IconAppsPlatform {
  if (canPickScreenTimeApps()) return "ios";
  if (canPickAndroidApps()) return "android";
  return "web";
}

/**
 * A Smart Block selection conforms when the user picked exactly one app and
 * nothing else — no categories or web domains. We cannot verify bundle IDs
 * (opaque tokens), so counts are the only signal available.
 */
export function isConformingSmartBlockSelection(summary: Pick<
  NativeSelectionSummary,
  "applicationCount" | "categoryCount" | "webDomainCount"
>): boolean {
  return (
    summary.applicationCount === 1 &&
    summary.categoryCount === 0 &&
    summary.webDomainCount === 0
  );
}

export function selectionTotalCount(
  summary: Pick<NativeSelectionSummary, "applicationCount" | "categoryCount" | "webDomainCount">,
): number {
  return summary.applicationCount + summary.categoryCount + summary.webDomainCount;
}

export function toTargetSelection(
  selectionId: string,
  summary: Pick<
    NativeSelectionSummary,
    "applicationCount" | "categoryCount" | "webDomainCount"
  >,
): TargetSelection {
  return {
    id: selectionId,
    applicationCount: summary.applicationCount,
    categoryCount: summary.categoryCount,
    webDomainCount: summary.webDomainCount,
    updatedAt: Date.now(),
  };
}

/** Whether a target's block scope still matches Smart Block semantics (single app only). */
export function targetQualifiesAsSmartBlockScope(
  target: Pick<TargetConfig, "apps" | "domains" | "selection">,
): boolean {
  if (target.selection && selectionHasContent(target.selection)) {
    return isConformingSmartBlockSelection(target.selection);
  }
  return target.apps.length === 1 && target.domains.length === 0;
}

/** Rules tab badge: origin + exactly one target whose scope is still Smart-Block-conforming. */
export function ruleDisplaysSmartBlockBadge(
  rule: Pick<Rule, "source" | "targetIds">,
  targetsById: ReadonlyMap<string, Pick<Target, "apps" | "domains" | "selection">>,
): boolean {
  if (rule.source !== "smart_block") return false;
  if (rule.targetIds.length !== 1) return false;

  const target = targetsById.get(rule.targetIds[0]!);
  if (!target) return false;

  return targetQualifiesAsSmartBlockScope(target);
}

/** Targets tab badge: sole target of a rule that would show the Smart Block badge. */
export function targetDisplaysSmartBlockBadge(
  targetId: string,
  rules: ReadonlyArray<Pick<Rule, "source" | "targetIds">>,
  targetsById: ReadonlyMap<string, Pick<Target, "apps" | "domains" | "selection">>,
): boolean {
  return rules.some(
    (rule) =>
      rule.targetIds.length === 1 &&
      rule.targetIds[0] === targetId &&
      ruleDisplaysSmartBlockBadge(rule, targetsById),
  );
}

type SmartBlockReactivationSuggestion = {
  mode?: "activate" | "reactivate";
  existingRuleId?: string;
  displayName?: string;
  appIdentifier?: string;
};

/** Existing Smart Block target for this app with conforming single-app scope. */
export function findSmartBlockTargetForApp(
  app: { displayName: string; appIdentifier?: string },
  targets: ReadonlyArray<Target> | undefined,
): Target | undefined {
  if (!targets) return undefined;
  const appKey = targetAppKey(app);
  return targets.find(
    (target) =>
      target.apps.some((entry) => entry.key === appKey) &&
      targetQualifiesAsSmartBlockScope(target),
  );
}

/**
 * Second-metric activation: reuse an existing conforming Smart Block target
 * without running the Family Activity Picker again. Requires a prior conforming
 * iOS picker selection on the shared target (typed-app-only targets still need
 * the first iOS activation to run the picker).
 */
export function eligibleForOneTapSmartBlockTargetReuse(
  suggestion: SmartBlockReactivationSuggestion & {
    displayName: string;
    appIdentifier?: string;
  },
  targets: ReadonlyArray<Target> | undefined,
): boolean {
  if (suggestion.mode === "reactivate") return false;
  const target = findSmartBlockTargetForApp(suggestion, targets);
  if (!target?.selection || !selectionHasContent(target.selection)) return false;
  return isConformingSmartBlockSelection(target.selection);
}

/** Disabled rule still shows the Smart Block badge — safe to re-enable without iOS picker. */
export function eligibleForOneTapSmartBlockReactivation(
  suggestion: SmartBlockReactivationSuggestion,
  rules: ReadonlyArray<Rule> | undefined,
  targets: ReadonlyArray<Target> | undefined,
): boolean {
  if (suggestion.mode !== "reactivate" || !suggestion.existingRuleId) return false;
  if (!rules || !targets) return false;

  const rule = rules.find((entry) => entry._id === suggestion.existingRuleId);
  if (!rule) return false;

  const targetsById = new Map(targets.map((target) => [target._id, target]));
  return ruleDisplaysSmartBlockBadge(rule, targetsById);
}

/** Aggregate picker counts across targets, deduping shared selection ids (Rules tab pattern). */
export function aggregatePickerCounts(
  targets: ReadonlyArray<Pick<TargetConfig, "selection">>,
): Pick<NativeSelectionSummary, "applicationCount" | "categoryCount" | "webDomainCount"> {
  const seenSelections = new Set<string>();
  let applicationCount = 0;
  let categoryCount = 0;
  let webDomainCount = 0;

  for (const target of targets) {
    const selection = target.selection;
    if (!selection?.id || seenSelections.has(selection.id)) continue;
    seenSelections.add(selection.id);
    applicationCount += selection.applicationCount;
    categoryCount += selection.categoryCount;
    webDomainCount += selection.webDomainCount;
  }

  return { applicationCount, categoryCount, webDomainCount };
}

/** Per-target picker constraint for Apple Search icons (domains never suppress). */
function targetPickerQualifiesForAppleSearchIcon(
  selection: TargetSelection | undefined,
): boolean {
  if (!selection || !selectionHasContent(selection)) return true;
  return selection.categoryCount === 0 && selection.applicationCount <= 1;
}

function dedupeAppsFromTargetList(
  targets: ReadonlyArray<Pick<TargetConfig, "apps">>,
): TargetApp[] {
  const seen = new Set<string>();
  const result: TargetApp[] = [];
  for (const target of targets) {
    for (const app of target.apps) {
      if (seen.has(app.key)) continue;
      seen.add(app.key);
      result.push(app);
    }
  }
  return result;
}

/** Rules tab Objective card: show App Store–resolved icon for Smart Block typed apps on iOS. */
export function targetShowsAppleSearchIcon(
  target: Pick<TargetConfig, "apps" | "selection">,
  canPickScreenTimeApps: boolean,
): boolean {
  if (!canPickScreenTimeApps) return false;
  if (target.apps.length === 0) return false;
  return targetPickerQualifiesForAppleSearchIcon(target.selection);
}

export function appleSearchIconAppsForTarget(
  target: Pick<TargetConfig, "apps" | "selection">,
  canPickScreenTimeApps: boolean,
): TargetApp[] {
  if (!targetShowsAppleSearchIcon(target, canPickScreenTimeApps)) return [];
  return target.apps;
}

/** Rules tab Active Rule card: same visibility rules with picker counts aggregated across targets. */
export function ruleShowsAppleSearchIcon(
  rule: Pick<Rule, "targetIds">,
  targetsById: ReadonlyMap<string, Pick<Target, "apps" | "selection">>,
  canPickScreenTimeApps: boolean,
): boolean {
  if (!canPickScreenTimeApps) return false;

  const targets = rule.targetIds
    .map((id) => targetsById.get(id))
    .filter((target): target is Pick<Target, "apps" | "selection"> => Boolean(target));

  if (!targets.some((target) => target.apps.length > 0)) return false;

  const { applicationCount, categoryCount } = aggregatePickerCounts(targets);
  return applicationCount <= 1 && categoryCount === 0;
}

export function appleSearchIconAppsForRule(
  rule: Pick<Rule, "targetIds">,
  targetsById: ReadonlyMap<string, Pick<Target, "apps" | "selection">>,
  canPickScreenTimeApps: boolean,
): TargetApp[] {
  if (!ruleShowsAppleSearchIcon(rule, targetsById, canPickScreenTimeApps)) return [];

  const qualifyingTargets = rule.targetIds
    .map((id) => targetsById.get(id))
    .filter((target): target is Pick<Target, "apps" | "selection"> => Boolean(target))
    .filter(
      (target) => target.apps.length > 0 && targetPickerQualifiesForAppleSearchIcon(target.selection),
    );

  return dedupeAppsFromTargetList(qualifyingTargets);
}

function androidResolvableAppsFromTarget(
  target: Pick<TargetConfig, "apps">,
): TargetApp[] {
  return target.apps.filter((app) => isAndroidPackageName(app.appIdentifier));
}

/** Android Rules tab: all typed apps with resolvable package names (no Smart Block ≤1-app gate). */
export function androidIconAppsForTarget(
  target: Pick<TargetConfig, "apps">,
): TargetApp[] {
  return androidResolvableAppsFromTarget(target);
}

/** Android rule cards: deduped unique package apps across all linked targets. */
export function androidIconAppsForRule(
  rule: Pick<Rule, "targetIds">,
  targetsById: ReadonlyMap<string, Pick<Target, "apps">>,
): TargetApp[] {
  const targets = rule.targetIds
    .map((id) => targetsById.get(id))
    .filter((target): target is Pick<Target, "apps"> => Boolean(target));
  return dedupeAppsFromTargetList(targets).filter((app) =>
    isAndroidPackageName(app.appIdentifier),
  );
}

/**
 * Unified icon list for Rules previews. iOS delegates to Smart Block / Apple Search
 * gating unchanged; Android returns all resolvable launcher icons from typed apps.
 */
export function iconAppsForTarget(
  target: Pick<TargetConfig, "apps" | "selection">,
  platform: IconAppsPlatform = resolveIconAppsPlatform(),
): TargetApp[] {
  if (platform === "ios") {
    return appleSearchIconAppsForTarget(target, true);
  }
  if (platform === "android") {
    return androidIconAppsForTarget(target);
  }
  return [];
}

export function iconAppsForRule(
  rule: Pick<Rule, "targetIds">,
  targetsById: ReadonlyMap<string, Pick<Target, "apps" | "selection">>,
  platform: IconAppsPlatform = resolveIconAppsPlatform(),
): TargetApp[] {
  if (platform === "ios") {
    return appleSearchIconAppsForRule(rule, targetsById, true);
  }
  if (platform === "android") {
    return androidIconAppsForRule(rule, targetsById);
  }
  return [];
}

/** Insight tab: use full iOS picker flow unless reactivating or reusing an existing target. */
export function shouldUseIosSmartBlockActivationFlow(
  canPickScreenTimeApps: boolean,
  suggestion: (SmartBlockReactivationSuggestion & {
    displayName?: string;
    appIdentifier?: string;
  }) | null,
  rules: ReadonlyArray<Rule> | undefined,
  targets: ReadonlyArray<Target> | undefined,
): boolean {
  if (!canPickScreenTimeApps) return false;
  if (!suggestion) return true;
  if (suggestion.mode === "reactivate") {
    return !eligibleForOneTapSmartBlockReactivation(suggestion, rules, targets);
  }
  if (
    suggestion.displayName &&
    eligibleForOneTapSmartBlockTargetReuse(
      { ...suggestion, displayName: suggestion.displayName },
      targets,
    )
  ) {
    return false;
  }
  return true;
}
